'use client';

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import {
  NotificationWebSocketClient,
  type NotificationMessage,
  type MessageEvent,
} from '@/lib/websocket/notificationClient';
import { useAuth } from './AuthContext';

interface NotificationWebSocketContextType {
  client: NotificationWebSocketClient | null;
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  onNotification: (handler: (notification: NotificationMessage) => void) => () => void;
  onMessage: (handler: (message: MessageEvent) => void) => () => void;
}

const NotificationWebSocketContext = createContext<NotificationWebSocketContextType | undefined>(undefined);

// Helper function to get WebSocket URL from API base URL
function getWebSocketBaseUrl(): string {
  if (typeof window === 'undefined') {
    return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/api';
  }

  const apiUrl = process.env.NEXT_PUBLIC_API_URL;
  const isDevelopment = process.env.NODE_ENV === 'development';

  if (isDevelopment) {
    // In development, WebSocket needs to connect directly to backend
    // WebSocket upgrades don't work through Next.js rewrites
    // Use NEXT_PUBLIC_API_URL if set, otherwise connect directly to backend port
    if (apiUrl) {
      // Convert HTTP/HTTPS URL to WS/WSS
      if (apiUrl.startsWith('https://')) {
        return apiUrl.replace('https://', 'wss://');
      } else if (apiUrl.startsWith('http://')) {
        return apiUrl.replace('http://', 'ws://');
      }
      return apiUrl;
    }
    // Default: connect directly to backend on port 8080
    return 'http://localhost:8080/api';
  }

  // Production: convert HTTP/HTTPS URL to WS/WSS
  if (apiUrl) {
    if (apiUrl.startsWith('https://')) {
      return apiUrl.replace('https://', 'wss://');
    } else if (apiUrl.startsWith('http://')) {
      return apiUrl.replace('http://', 'ws://');
    }
    return apiUrl;
  }

  return '/api';
}

export function NotificationWebSocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const clientRef = useRef<NotificationWebSocketClient | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Initialize client
  useEffect(() => {
    const baseUrl = getWebSocketBaseUrl();
    console.log('NotificationWebSocket: Initializing with base URL', baseUrl);
    clientRef.current = new NotificationWebSocketClient(baseUrl);

    return () => {
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
    };
  }, []);

  // Connect/disconnect based on authentication
  useEffect(() => {
    if (!clientRef.current) return;

    if (isAuthenticated) {
      // Get token and connect
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      if (token) {
        clientRef.current.setToken(token);
        clientRef.current.connect();
      }
    } else {
      // Disconnect when logged out
      clientRef.current.disconnect();
    }
  }, [isAuthenticated]);

  // Update connection state
  useEffect(() => {
    if (!clientRef.current) return;

    const updateConnectionState = () => {
      setIsConnected(clientRef.current?.isConnected() || false);
    };

    const unsubscribeConnect = clientRef.current.onConnect(updateConnectionState);
    const unsubscribeDisconnect = clientRef.current.onDisconnect(updateConnectionState);

    return () => {
      unsubscribeConnect();
      unsubscribeDisconnect();
    };
  }, []);

  const connect = () => {
    if (clientRef.current) {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      if (token) {
        clientRef.current.setToken(token);
        clientRef.current.connect();
      }
    }
  };

  const disconnect = () => {
    if (clientRef.current) {
      clientRef.current.disconnect();
    }
  };

  const onNotification = (handler: (notification: NotificationMessage) => void) => {
    if (!clientRef.current) {
      return () => {}; // Return no-op unsubscribe function
    }
    return clientRef.current.onNotification(handler);
  };

  const onMessage = (
    handler: (message: MessageEvent) => void
  ) => {
    if (!clientRef.current) {
      return () => {};
    }
    return clientRef.current.onMessage(handler);
  };

  const contextValue: NotificationWebSocketContextType = {
    client: clientRef.current,
    isConnected,
    connect,
    disconnect,
    onNotification,
    onMessage,
  };

  return (
    <NotificationWebSocketContext.Provider value={contextValue}>
      {children}
    </NotificationWebSocketContext.Provider>
  );
}

export function useNotificationWebSocket() {
  const context = useContext(NotificationWebSocketContext);
  if (context === undefined) {
    throw new Error('useNotificationWebSocket must be used within a NotificationWebSocketProvider');
  }
  return context;
}

